import { ReactNode, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";

import { useTranslation } from "react-i18next";
import { theme } from "src/assets/theme";

interface StripeContextProviderProps {
  children: ReactNode;
  billingModel?: "one_time" | "subscription";
  amount?: number;
  customerSessionToken?: string;
}

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ""
);

const StripeContextProvider = ({
  children,
  billingModel,
  amount,
  customerSessionToken,
}: StripeContextProviderProps) => {
  const { i18n } = useTranslation();

  const appearance: StripeElementsOptions["appearance"] = useMemo(
    () => ({
      theme: "stripe",
      variables: {
        colorPrimary: theme.palette.primary.main,
        borderRadius: "10px",
      },
    }),
    []
  );

  // Properly type the options object
  const stripeOptions: StripeElementsOptions = useMemo(() => {
    const paymentMethods = ["card"];

    if (billingModel === "one_time") {
      paymentMethods.push("klarna");
    }

    const price = (amount ?? 0) * 100;

    return {
      appearance,
      locale: i18n.language as StripeElementsOptions["locale"],
      currency: "usd",
      mode: billingModel === "one_time" ? "payment" : "setup",
      amount: price,
      customerSessionClientSecret: customerSessionToken,
      payment_method_types: paymentMethods,
      setup_future_usage:
        billingModel === "subscription" ? "off_session" : "on_session",
    };
  }, [billingModel, amount, appearance, i18n.language, customerSessionToken]);

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      {children}
    </Elements>
  );
};

export default StripeContextProvider;
